import { Cost, CostBuilder, CostEffect } from "./util/cost"
import { JsonHelper, type JsonMap, type JsonValue } from "./util/json"
import { isZero } from "./util/number"
import Percent from "./util/percent"

export interface CapitalRequirementsPositionEntry {
  readonly instrumentSymbol: string | null
  readonly instrumentType: string | null
  readonly quantity: number
}

export interface CapitalRequirementsGroup {
  readonly description: string
  readonly underlyingSymbol: string | null
  readonly underlyingType: string | null
  readonly marginCalculationType: string | null
  readonly riskChargeScenarioSource: string | null
  readonly marginRequirement: Cost | null
  readonly initialRequirement: Cost | null
  readonly regTMarginRequirement: Cost | null
  readonly maintenanceRequirement: Cost | null
  readonly buyingPower: Cost | null
  readonly alternativeMinimumCharge: Cost | null
  readonly minimumCharge: Cost | null
  readonly alternativeVegaMinimumCharge: Cost | null
  readonly alternativeExtrinsicMinimumCharge: Cost | null
  readonly riskCharge: Cost | null
  readonly baseRiskCharge: Cost | null
  readonly expectedPriceRangeCharge: Cost | null
  readonly riskChargeScenarioPercent: Percent
  readonly expectedPriceRangeUpPercent: Percent
  readonly expectedPriceRangeDownPercent: Percent
  readonly pointOfNoReturnPercent: Percent
  readonly priceIncreasePercent: Percent
  readonly priceDecreasePercent: Percent
  readonly includesWorkingOrder: boolean
  readonly hasElevatedEarningsRequirements: boolean
  readonly groups: readonly CapitalRequirementsGroup[]
  readonly positionEntries: readonly CapitalRequirementsPositionEntry[]
}

export interface CapitalRequirements {
  readonly accountNumber: string | null
  readonly description: string | null
  readonly marginCalculationType: string | null
  readonly optionLevel: string | null
  readonly marginRequirement: Cost | null
  readonly initialRequirement: Cost | null
  readonly maintenanceRequirement: Cost | null
  readonly optionBuyingPower: Cost | null
  readonly regTMarginRequirement: Cost | null
  readonly regTOptionBuyingPower: Cost | null
  readonly longBondValue: number
  readonly bondMarginRequirement: Cost | null
  readonly lastStateTimestamp: number
  readonly groups: readonly CapitalRequirementsGroup[]
  readonly totalBuyingPower: Cost
}

const BOND_GROUP_DESCRIPTION = "Bond"

const NO_WIRE_FIELDS = new JsonHelper({})

const JAVA_DOUBLE_GRAMMAR =
  /^\s*[+-]?(NaN|Infinity|((\d+\.?\d*)|(\.\d+))([eE][+-]?\d+)?[dDfF]?)\s*$/

function presentWireValue(helper: JsonHelper, key: string): JsonValue {
  const value = (helper.toJson() as JsonMap)[key]
  return value === undefined || value === "" ? null : value
}

function optionalString(helper: JsonHelper, key: string): string | null {
  const value = helper.getString(key)
  return value === "" ? null : value
}

function numberOrNaN(value: JsonValue): number {
  if (typeof value === "number") {
    return value
  }

  return typeof value === "string" && JAVA_DOUBLE_GRAMMAR.test(value)
    ? Number.parseFloat(value)
    : NaN
}

function optionalNumber(helper: JsonHelper, key: string): number {
  return numberOrNaN(presentWireValue(helper, key))
}

function optionalPercent(helper: JsonHelper, key: string): Percent {
  return Percent.fromDecimal(optionalNumber(helper, key))
}

function toCostEffect(key: string, effect: JsonValue): CostEffect {
  switch (effect) {
    case "Debit":
      return CostEffect.Debit
    case "Credit":
      return CostEffect.Credit
    case "None":
      return CostEffect.NoCost
    default:
      throw new Error(`Unknown ${key}: ${JSON.stringify(effect)}`)
  }
}

function optionalCost(helper: JsonHelper, key: string): Cost | null {
  const effectKey = `${key}-effect`
  const wireValue = presentWireValue(helper, key)
  const wireEffect = presentWireValue(helper, effectKey)
  if (wireValue === null || wireEffect === null) {
    return null
  }

  const value = numberOrNaN(wireValue)
  const effect = toCostEffect(effectKey, wireEffect)
  if (isZero(value)) {
    return Cost.ZERO
  }

  if (!Number.isFinite(value) || effect === CostEffect.NoCost) {
    throw new Error(
      `Invalid ${key}: ${JSON.stringify(wireValue)} ${JSON.stringify(wireEffect)}`
    )
  }

  return value < 0
    ? Cost.createOpposite(-value, effect)
    : Cost.create(value, effect)
}

function parsePositionEntry(
  helper: JsonHelper
): CapitalRequirementsPositionEntry {
  return {
    instrumentSymbol: optionalString(helper, "instrument-symbol"),
    instrumentType: optionalString(helper, "instrument-type"),
    quantity: optionalNumber(helper, "quantity"),
  }
}

function parseGroupFields(
  helper: JsonHelper
): Omit<CapitalRequirementsGroup, "description"> {
  return {
    underlyingSymbol: optionalString(helper, "underlying-symbol"),
    underlyingType: optionalString(helper, "underlying-type"),
    marginCalculationType: optionalString(helper, "margin-calculation-type"),
    riskChargeScenarioSource: optionalString(
      helper,
      "risk-charge-scenario-source"
    ),
    marginRequirement: optionalCost(helper, "margin-requirement"),
    initialRequirement: optionalCost(helper, "initial-requirement"),
    regTMarginRequirement: optionalCost(helper, "reg-t-margin-requirement"),
    maintenanceRequirement: optionalCost(helper, "maintenance-requirement"),
    buyingPower: optionalCost(helper, "buying-power"),
    alternativeMinimumCharge: optionalCost(
      helper,
      "alternative-minimum-charge"
    ),
    minimumCharge: optionalCost(helper, "minimum-charge"),
    alternativeVegaMinimumCharge: optionalCost(
      helper,
      "alternative-vega-minimum-charge"
    ),
    alternativeExtrinsicMinimumCharge: optionalCost(
      helper,
      "alternative-extrinsic-minimum-charge"
    ),
    riskCharge: optionalCost(helper, "risk-charge"),
    baseRiskCharge: optionalCost(helper, "base-risk-charge"),
    expectedPriceRangeCharge: optionalCost(
      helper,
      "expected-price-range-charge"
    ),
    riskChargeScenarioPercent: optionalPercent(
      helper,
      "risk-charge-scenario-percent"
    ),
    expectedPriceRangeUpPercent: optionalPercent(
      helper,
      "expected-price-range-up-percent"
    ),
    expectedPriceRangeDownPercent: optionalPercent(
      helper,
      "expected-price-range-down-percent"
    ),
    pointOfNoReturnPercent: optionalPercent(
      helper,
      "point-of-no-return-percent"
    ),
    priceIncreasePercent: optionalPercent(helper, "price-increase-percent"),
    priceDecreasePercent: optionalPercent(helper, "price-decrease-percent"),
    includesWorkingOrder: helper.getBoolean("includes-working-order"),
    hasElevatedEarningsRequirements: helper.getBoolean(
      "has-elevated-earnings-requirements"
    ),
    groups: parseDescribedGroups(helper),
    positionEntries: helper
      .getChildren("position-entries")
      .map(parsePositionEntry),
  }
}

function parseDescribedGroups(helper: JsonHelper): CapitalRequirementsGroup[] {
  return helper.getChildren("groups").flatMap((child) => {
    const description = optionalString(child, "description")
    return description === null
      ? []
      : [{ description, ...parseGroupFields(child) }]
  })
}

function bondGroup(marginRequirement: Cost): CapitalRequirementsGroup {
  return {
    ...parseGroupFields(NO_WIRE_FIELDS),
    description: BOND_GROUP_DESCRIPTION,
    marginRequirement,
  }
}

function netBuyingPower(groups: readonly CapitalRequirementsGroup[]): Cost {
  return groups.reduce(
    (total, { buyingPower }) =>
      buyingPower === null ? total : total.adjust(buyingPower),
    new CostBuilder()
  ).toCost
}

export function parseCapitalRequirements(
  report: JsonHelper
): CapitalRequirements {
  const groups = parseDescribedGroups(report)
  const bondMarginRequirement = optionalCost(report, "bond-margin-requirement")
  if (bondMarginRequirement !== null && !isZero(bondMarginRequirement.value)) {
    groups.push(bondGroup(bondMarginRequirement))
  }

  return {
    accountNumber: optionalString(report, "account-number"),
    description: optionalString(report, "description"),
    marginCalculationType: optionalString(report, "margin-calculation-type"),
    optionLevel: optionalString(report, "option-level"),
    marginRequirement: optionalCost(report, "margin-requirement"),
    initialRequirement: optionalCost(report, "initial-requirement"),
    maintenanceRequirement: optionalCost(report, "maintenance-requirement"),
    optionBuyingPower: optionalCost(report, "option-buying-power"),
    regTMarginRequirement: optionalCost(report, "reg-t-margin-requirement"),
    regTOptionBuyingPower: optionalCost(report, "reg-t-option-buying-power"),
    longBondValue: optionalNumber(report, "long-bond-value"),
    bondMarginRequirement,
    lastStateTimestamp: optionalNumber(report, "last-state-timestamp"),
    groups,
    totalBuyingPower: netBuyingPower(groups),
  }
}
